<template>
  <div class="bubble">
    <b-row align-h="end">
      <b-col cols="3">
        <b-icon-trash-fill
          style="color: #c15be6"
          class="garbage"
          @click="deleteCharacter()"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-button
          v-if="!loading"
          size="lg"
          style="background: none; border: none; font-size: 1.5rem"
          class="name"
          @click="route()"
        >
          {{ name ? name : "Crescent Player" }}
        </b-button>
        <b-button
          v-if="loading"
          style="background: none; border: none; font-size: 1.5rem"
          disabled
        >
          <b-spinner small type="grow" style="color: #dfdfdf"></b-spinner>
          Loading... {{ name ? name : "Crescent Player" }}
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { getAttributes } from "@/common/character/attributes";
import { getExceptional } from "@/common/character/exceptional";
import { getDetails } from "@/common/character/details";
import { getBuild } from "@/common/character/build";
import { getSpells } from "@/common/character/spells";
import { getMartialPerks } from "@/common/character/martialPerks";
import { getSkills } from "@/common/character/skills";
import { getStatusEffects } from "@/common/character/status";
import { getResistances } from "@/common/character/resistances";
import { getDamageReductions } from "@/common/character/damageReductions";
import { getInventory } from "@/common/character/inventory";
import { deleteCharacter } from "@/common/authentication/character";
import { getWeaponSkills } from "@/common/character/weaponSkills";
import { getDesign } from "@/common/character/design";
import { getMartialPerkGain } from "@/common/character/martialPerkGain";

export default {
  name: "CharacterNav",
  props: {
    id: { type: Number, default: 90000000000 },
    name: {
      type: [String, null],
      default: "Crescent Player",
    },
  },

  data: function () {
    return {
      loading: false,
    };
  },
  methods: {
    deleteCharacter: function () {
      deleteCharacter(this.id).then(() => {
        window.location.reload();
      });
    },
    route: function () {
      const route = "/character/" + this.id;
      this.$router.push(route);
      this.loading = false;
    },
    clear: function () {
      this.clearAttrs();
      this.clearBuild();
      this.clearDesign();
      this.clearExceptionals();
      this.clearDetails();
      this.clearDivineSpellgroups();
      this.clearElementalSpells();
      this.clearInventory();
      this.clearSkills();
      this.clearWeaponSkills();
      this.clearMartialPerks();
      this.clearStatus();
    },
    clearAttrs() {
      const data = {
        agility: 0,
        charisma: 0,
        health: 0,
        intelligence: 0,
        perception: 0,
        power: 0,
        strength: 0,
        willpower: 0,
        tempAgility: 0,
        tempCharisma: 0,
        tempHealth: 0,
        tempIntelligence: 0,
        tempPerception: 0,
        tempPower: 0,
        tempStrength: 0,
        tempWillpower: 0,
      };
      this.$store.commit("character/attributes/setAttributes", data);
    },
    clearBuild() {
      this.$store.commit("character/build/setSkill1", "");
      this.$store.commit("character/build/setSkill2", "");
      this.$store.commit("character/build/setMovespeedMod", 0);
      this.$store.commit("character/build/setMana", 0);
      this.$store.commit("character/build/setHp", 0);
      this.$store.commit("character/build/setDefenseValues", {
        armor: 0,
        movement: 10,
        shield: 0,
        bonus: 0,
      });
      this.$store.commit("character/build/setSpentAbilityPoints", 0);
      this.$store.commit("character/build/setArchetype", "");
      this.$store.commit("character/build/setAbilityPointTotal", 10);
      this.$store.commit("character/build/setHp", 0);
      this.$store.commit("character/build/setKnownAbilities", []);
    },
    clearDetails() {
      this.$store.commit("character/details/setAge", 0);
      this.$store.commit("character/details/setBackground", "");
      this.$store.commit("character/details/setBackstory", "");
      this.$store.commit("character/details/setImage", "");
      this.$store.commit("character/details/setEyeColor", "");
      this.$store.commit("character/details/setGender", "");
      this.$store.commit("character/details/setHairColor", "");
      this.$store.commit("character/details/setJournal", "");
      this.$store.commit("character/details/setMotivation", "");
      this.$store.commit("character/details/setName", "Crescent Character");
      this.$store.commit("character/details/setNotes", "");
      this.$store.commit("character/details/setWeight", "0");
    },
    clearDesign() {
      this.$store.commit("character/details/setDesignProfile", {
        pictorial: true,
        name: "Default Profile",
        defaultPageBackground: "#dfdfdf",
        defaultTextLight: "#dfdfdf",
        defaultTextDark: "#000000",
        backgroundAndBorder: "#422c58",
        lightBackground: "#f1eef1",
        alertDefault: "#c15be6",
        font: "Bahnschrift",
      });
    },
    clearExceptionals() {
      this.$store.commit("character/exceptional/setExceptionalAgility", 0);
      this.$store.commit("character/exceptional/setExceptionalCharisma", 0);
      this.$store.commit("character/exceptional/setExceptionalHealth", 0);
      this.$store.commit("character/exceptional/setExceptionalIntelligence", 0);
      this.$store.commit("character/exceptional/setExceptionalPerception", 0);
      this.$store.commit("character/exceptional/setExceptionalPower", 0);
      this.$store.commit("character/exceptional/setExceptionalStrength", 0);
      this.$store.commit("character/exceptional/setExceptionalWillpower", 0);
      this.$store.commit("character/exceptional/setTempExceptionalAgility", 0);
      this.$store.commit("character/exceptional/setTempExceptionalCharisma", 0);
      this.$store.commit("character/exceptional/setTempExceptionalHealth", 0);
      this.$store.commit(
        "character/exceptional/setTempExceptionalIntelligence",
        0
      );
      this.$store.commit(
        "character/exceptional/setTempExceptionalPerception",
        0
      );
      this.$store.commit("character/exceptional/setTempExceptionalPower", 0);
      this.$store.commit("character/exceptional/setTempExceptionalStrength", 0);
      this.$store.commit(
        "character/exceptional/setTempExceptionalWillpower",
        0
      );
    },
    clearInventory() {
      this.$store.commit("character/inventory/setCoin", 0);
      this.$store.commit("character/inventory/setInventory", []);
      this.$store.commit("character/inventory/setMagicItems", []);
    },
    clearMartialPerks() {
      this.$store.commit("character/martialPerks/clearMartialPerks");
    },
    clearSkills() {
      this.$store.commit("character/skills/clearSkills");
    },
    clearWeaponSkills() {
      this.$store.commit("character/skills/clearWeaponSkills");
    },
    clearElementalSpells() {
      this.$store.commit("character/spells/clearElementalSpells");
      this.$store.commit("character/spells/setArcaneBattery", 0);
    },
    clearDivineSpellgroups() {
      this.$store.commit("character/spells/clearDivineSpells");
    },
    clearStatus() {
      this.$store.commit("character/status/clearDr");
      this.$store.commit("character/status/clearResist");
      this.$store.commit("character/status/clearStatus");
    },
    init: async function (character_id) {
      this.loading = true;
      await this.clear();
      const response = await getAttributes(character_id);
      if (response.data) {
        this.setAttributes(response.data);
      }

      const exceptionalResponse = await getExceptional(character_id);
      if (exceptionalResponse.data) {
        this.setExceptionals(exceptionalResponse.data);
      }

      const detailsResponse = await getDetails(character_id);
      if (detailsResponse.data) {
        for (let detail of detailsResponse.data) {
          this.$store.commit("character/details/setDetails", detail);
        }
      }
      const buildResponse = await getBuild(character_id);
      if (buildResponse.data) {
        this.setBuild(buildResponse.data);
      }

      const spellResponse = await getSpells(character_id);
      if (spellResponse.data) {
        this.setSpells(spellResponse.data);
      }

      const perkResponse = await getMartialPerks(character_id);
      if (perkResponse.data) {
        this.setMartialPerks(perkResponse.data);
      }

      const gainResponse = await getMartialPerkGain(character_id);
      if (gainResponse.data) {
        this.setMartialGain(gainResponse.data);
      }
      const skillResponse = await getSkills(character_id);
      if (skillResponse.data) {
        this.setSkills(skillResponse.data);
      }

      const statusResponse = await getStatusEffects(character_id);
      if (statusResponse.data) {
        this.setStatusEffects(statusResponse.data);
      }

      const resistResponse = await getResistances(character_id);
      if (resistResponse.data) {
        this.setResistances(resistResponse.data);
      }

      const damageReductionResponse = await getDamageReductions(character_id);
      if (damageReductionResponse.data) {
        this.setDamageReductions(damageReductionResponse.data);
      }

      const inventoryResponse = await getInventory(character_id);
      if (inventoryResponse.data) {
        this.setInventory(inventoryResponse.data);
      }

      const weaponSkillResponse = await getWeaponSkills(character_id);
      if (weaponSkillResponse.data) {
        this.setWeaponSkills(weaponSkillResponse.data);
      }

      const styleResponse = await getDesign(character_id);
      if (styleResponse.data) {
        this.setDesign(styleResponse.data);
      }
      const route = "/character/" + character_id;
      await this.$router.push(route);
      this.loading = false;
    },
    setAttributes: function (attributes) {
      for (let attribute of attributes) {
        let newData = {
          name: attribute.name,
          value: attribute.value,
        };
        this.$store.commit("character/attributes/setDBAttributes", newData);
      }
    },
    setExceptionals: function (exceptionals) {
      for (let exceptional of exceptionals) {
        let newData = {
          name: exceptional.name,
          level: exceptional.level,
        };
        this.$store.commit("character/exceptional/setDBExceptionals", newData);
      }
    },
    setBuild: function (builds) {
      for (let build of builds) {
        this.$store.commit(
          "character/build/setAbilityPointTotal",
          build.ability_points_total
        );
        //build.ability_points_spent
        const defenseValues = {
          armor: build.defense_values_armor,
          movement: build.defense_values_movement,
          shield: build.defense_values_shield,
          bonus: build.defense_values_bonus,
        };
        this.$store.commit("character/build/setDefenseValues", defenseValues);
        this.$store.commit("character/build/setHp", build.hp);
        this.$store.commit("character/build/setHpMax", build.hp_max);
        this.$store.commit("character/build/setMana", build.mana);
        this.$store.commit("character/build/setManaMax", build.mana_max);
        this.$store.commit("character/build/setMovespeed", build.movespeed);
        this.$store.commit("character/build/setSkill1", build.origin_skill_1);
        this.$store.commit("character/build/setSkill2", build.origin_skill_2);
        this.$store.commit("character/build/setArchetype", build.archetype);
        this.$store.commit(
          "character/build/setMovespeedMod",
          build.movespeed_modifier
        );
      }
    },
    setSpells: function (spells) {
      for (let spell of spells) {
        if (spell.category === "elementalSpellgroups") {
          const elemental =
            this.$store.getters["character/spells/elementalSpellgroups"];
          let spellCommit = this.getSpellPosition(spell, elemental);
          if (spellCommit !== -1) {
            this.$store.commit(
              "character/spells/setElementalSpell",
              spellCommit
            );
          }
        } else if (spell.category === "divineSpellgroups") {
          const divine =
            this.$store.getters["character/spells/divineSpellgroups"];
          let spellCommit = this.getSpellPosition(spell, divine);
          if (spellCommit !== -1) {
            this.$store.commit("character/spells/setDivineSpell", spellCommit);
          }
        } else if (spell.category === "Arcane Battery") {
          this.$store.commit("character/spells/setArcaneBattery", spell.rank);
        }
      }
    },
    getSpellPosition: function (spell, category) {
      for (let i = 0; i < category[spell.group].length; i++) {
        if (
          spell.name === category[spell.group][i].name &&
          spell.rank === category[spell.group][i].rank
        ) {
          return { known: true, i: i, spellgroup: spell.group };
        }
      }
      return -1;
    },
    setMartialPerks: function (perks) {
      const martialPerks =
        this.$store.getters["character/martialPerks/martialPerks"];
      for (let perk of perks) {
        let perkCommit = this.getPerkPosition(perk, martialPerks);
        if (perkCommit !== -1) {
          this.$store.commit("character/martialPerks/togglePerk", perkCommit);
        }
      }
    },
    setMartialGain: function (gains) {
      for (let g of gains) {
        this.$store.commit("character/martialPerks/setMartialGainFromDb", g);
      }
    },
    getPerkPosition: function (perk, category) {
      for (let i = 0; i < category[perk.type].length; i++) {
        if (
          perk.name === category[perk.type][i].name &&
          perk.rank === category[perk.type][i].rank
        ) {
          return { known: true, i: i, perkGroup: perk.type };
        }
      }
      return -1;
    },
    setSkills: function (skills) {
      for (let skill of skills) {
        this.$store.commit("character/skills/setSkill", skill);
      }
    },
    setStatusEffects: function (statusEffects) {
      for (let status of statusEffects) {
        let temp = {
          name: status.name,
          check: status.check,
        };
        this.$store.commit("character/status/setStatusEffectFromDb", temp);
      }
    },
    setResistances: function (resistances) {
      for (let resistance of resistances) {
        let temp = {
          name: resistance.name,
          level: resistance.level,
        };
        this.$store.commit("character/status/setResistanceFromDb", temp);
      }
    },
    setDamageReductions: function (damageReductions) {
      for (let damageReduction of damageReductions) {
        let temp = {
          name: damageReduction.name,
          level: damageReduction.level,
        };
        this.$store.commit("character/status/setDamageReductionFromDb", temp);
      }
    },
    setInventory: function (items) {
      for (let item of items) {
        if (!item.is_magic) {
          if (item.name === "platinum_coins") {
            let amount = item.count * 1000;
            this.$store.commit("character/inventory/earnCoin", amount);
          } else if (item.name === "gold_coins") {
            let amount = item.count * 100;
            this.$store.commit("character/inventory/earnCoin", amount);
          } else if (item.name === "silver_coins") {
            let amount = item.count * 10;
            this.$store.commit("character/inventory/earnCoin", amount);
          } else if (item.name === "copper_coins") {
            let amount = item.count;
            this.$store.commit("character/inventory/earnCoin", amount);
          } else {
            let temp = {
              itemName: item.name,
              amount: item.count,
              description: item.description,
            };
            this.$store.commit("character/inventory/addInventory", temp);
          }
        } else {
          let temp = {
            itemName: item.name,
            description: item.description,
            attuned: item.count === 2,
          };
          this.$store.commit("character/inventory/addMagicItem", temp);
        }
      }
    },
    setWeaponSkills: function (skills) {
      for (let skill of skills) {
        this.$store.commit("character/skills/setWeaponSkill", skill);
      }
    },
    setDesign: function (design) {
      let designObject = {
        pictorial: design[0].pictoral,
        defaultPageBackground: design[0].pageBackdrop,
        defaultTextLight: design[0].textShade1,
        defaultTextDark: design[0].textShade2,
        backgroundAndBorder: design[0].primaryBacking,
        lightBackground: design[0].secondaryBacking,
        alertDefault: design[0].alertShade,
        font: design[0].font,
      };
      this.$store.commit("character/details/setDesignProfile", designObject);
    },
  },
};
</script>

<style scoped>
.garbage:hover {
  cursor: pointer;
}
a {
  color: #dfdfdf;
  font-size: 24px;
}
div.bubble {
  display: flex;
  flex-direction: column;
  height: 200px;
  width: 200px;
  border-radius: 20px;
  background: #422c58;
  color: #dfdfdf;
  font-size: 24px;
  padding: 1rem;
}
div.growSpacer {
  height: 10px;
  width: 10px;
  flex-grow: 1;
}
</style>
